import React, { useEffect, useMemo, useState } from "react";
import { ClientLayout } from "@/components/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { getShopProfile, getShopCatalog, shopPlaceOrder, fmt } from "@/lib/api";
import { ShoppingCart, Trash2, Package, Plus } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { isAllowedWeekday, nextAllowedDate, weekdayNames } from "@/lib/dates";

export default function ClientPortal() {
  const { t, td } = useT();
  const [profile, setProfile] = useState(null);
  const [catalog, setCatalog] = useState({ items: [], currency: "RON", price_level: 1 });
  const [cart, setCart] = useState({}); // key -> {product_type, unit, unit_price, quantity, available}
  const [qty, setQty] = useState({}); // key -> input value
  const [selUnit, setSelUnit] = useState({}); // product_type -> selected package unit
  const [placing, setPlacing] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");

  const load = async () => {
    const [p, c] = await Promise.all([getShopProfile(), getShopCatalog()]);
    setProfile(p); setCatalog(c);
    const dd = Array.isArray(p?.delivery_days) ? p.delivery_days : [0, 1, 2, 3, 4];
    setDeliveryDate((prev) => prev || nextAllowedDate(dd, new Date().toISOString().slice(0, 10)));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deliveryDays = Array.isArray(profile?.delivery_days) ? profile.delivery_days : [0, 1, 2, 3, 4];

  const keyOf = (it) => `${it.product_type}|${it.unit}`;

  // Group the catalog by product; each product offers its in-stock package types in a dropdown.
  // (Backend already hides bulk "kg" and out-of-stock non-packed goods; no-price items come
  //  through flagged orderable=false so they can be shown but not ordered.)
  const grouped = useMemo(() => {
    const m = {};
    for (const it of catalog.items || []) {
      (m[it.product_type] ||= []).push(it);
    }
    return Object.entries(m)
      .map(([product, units]) => ({ product, units }))
      .sort((a, b) => a.product.localeCompare(b.product));
  }, [catalog.items]);

  const addToCart = (it) => {
    const k = keyOf(it);
    if (it.unit_price == null) { toast.error(t("client.priceNotSet")); return; }
    const q = Number(qty[k]);
    if (!q || q <= 0) { toast.error(t("client.enterQty")); return; }
    if (!it.packed_on_order && q > it.available + 1e-6) { toast.error(t("client.onlyAvailable", { qty: fmt(it.available, 2), unit: it.unit })); return; }
    setCart((x) => ({ ...x, [k]: { product_type: it.product_type, unit: it.unit, unit_price: it.unit_price, quantity: q, available: it.available } }));
    toast.success(t("client.added", { product: td(it.product_type) }));
  };
  const rmFromCart = (k) => setCart((x) => { const n = { ...x }; delete n[k]; return n; });

  const cartItems = Object.entries(cart);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const total = useMemo(() => cartItems.reduce((s, [, v]) => s + v.quantity * v.unit_price, 0), [cart]);

  const placeOrder = async () => {
    if (cartItems.length === 0) { toast.error(t("client.emptyCartToast")); return; }
    if (!deliveryDate) { toast.error(t("orders.deliveryDateRequired")); return; }
    if (!isAllowedWeekday(deliveryDate, deliveryDays)) {
      toast.error(t("orders.deliveryDayNotAllowed", { days: weekdayNames(deliveryDays) }));
      return;
    }
    setPlacing(true);
    try {
      await shopPlaceOrder({ items: cartItems.map(([, v]) => ({ product_type: v.product_type, unit: v.unit, quantity: v.quantity })), requested_date: deliveryDate });
      toast.success(t("client.orderPlaced"));
      setCart({}); setQty({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("client.failedPlace")); }
    finally { setPlacing(false); }
  };

  return (
    <ClientLayout>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("client.shop")}</h1>
          <p className="text-sm text-muted-foreground">{t("client.yourPrices", { name: profile?.name || "", level: catalog.price_level })}</p>
        </div>
        <Badge className="bg-[hsl(var(--brand))] text-white">{t("client.levelPricing", { level: catalog.price_level })}</Badge>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          {grouped.length === 0 ? (
            <Card><CardContent className="flex flex-col items-center py-12 text-center">
              <Package className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("client.catalogEmpty")}</p>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {grouped.map(({ product, units }) => {
                const selectedUnit = selUnit[product] || units[0].unit;
                const it = units.find((u) => u.unit === selectedUnit) || units[0];
                const k = keyOf(it);
                const out = Number(it.available) <= 0;
                const noPrice = it.unit_price == null;
                const orderable = !noPrice && !out;
                return (
                  <Card key={product} data-testid={`catalog-${product}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium">{td(product)}</div>
                          <div className="text-xs text-muted-foreground">
                            {t("client.inStock")}
                          </div>
                        </div>
                        <div className="text-right">
                          {noPrice ? (
                            <Badge className="badge-warn" data-testid={`catalog-noprice-${product}`}>{t("client.priceNotSet")}</Badge>
                          ) : (
                            <>
                              <div className="font-mono text-lg font-semibold tabular-nums">{fmt(it.unit_price, 2)}</div>
                              <div className="text-[11px] text-muted-foreground">{catalog.currency}/{it.unit}</div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 space-y-1.5">
                        <Label className="text-xs text-muted-foreground">{t("client.packageType")}</Label>
                        <Select value={selectedUnit} onValueChange={(v) => setSelUnit((s) => ({ ...s, [product]: v }))}>
                          <SelectTrigger className="h-9" data-testid={`catalog-unit-${product}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {units.map((u) => (
                              <SelectItem key={u.unit} value={u.unit} data-testid={`catalog-unit-option-${product}-${u.unit}`}>
                                {u.unit}
                                {u.unit_price == null ? ` · ${t("client.priceNotSet")}` : ` · ${fmt(u.unit_price, 2)} ${catalog.currency}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="mt-2">
                        {out
                          ? <Badge className="badge-bad">{t("client.outOfStock")}</Badge>
                          : <Badge className="badge-ok">{t("client.available", { qty: fmt(it.available, 2), unit: it.unit })}</Badge>}
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        <Input type="number" className="h-9 text-right font-mono" placeholder={t("client.qtyPlaceholder")} value={qty[k] || ""} disabled={!orderable}
                          onChange={(e) => setQty((x) => ({ ...x, [k]: e.target.value }))} data-testid={`catalog-qty-${product}`} />
                        <Button size="sm" disabled={!orderable} onClick={() => addToCart(it)} data-testid={`catalog-add-${product}`}>
                          <Plus className="mr-1 h-4 w-4" /> {t("common.add")}
                        </Button>
                      </div>
                      {noPrice && <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid={`catalog-noprice-hint-${product}`}>{t("client.priceNotSetHint")}</p>}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-4">
          <Card className="sticky top-20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShoppingCart className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("client.yourOrder")} ({cartItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cartItems.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t("client.addProductsHint")}</p>
              ) : (
                <div className="space-y-3">
                  {cartItems.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-2" data-testid={`cart-item-${k}`}>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{td(v.product_type)}</div>
                        <div className="font-mono text-xs text-muted-foreground">{fmt(v.quantity, 2)} {v.unit} × {fmt(v.unit_price, 2)}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-sm tabular-nums">{fmt(v.quantity * v.unit_price, 2)}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => rmFromCart(k)} data-testid={`cart-remove-${k}`}>
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Separator />
                  <div className="flex items-center justify-between font-mono">
                    <span className="text-sm text-muted-foreground">{t("common.total")}</span>
                    <span className="text-lg font-semibold tabular-nums">{fmt(total, 2)} {catalog.currency}</span>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("orders.deliveryDate")} <span className="text-[hsl(var(--danger))]">*</span></Label>
                    <Input
                      type="date"
                      min={new Date().toISOString().slice(0, 10)}
                      value={deliveryDate}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v && !isAllowedWeekday(v, deliveryDays)) {
                          toast.error(t("orders.deliveryDayNotAllowed", { days: weekdayNames(deliveryDays) }));
                          return;
                        }
                        setDeliveryDate(v);
                      }}
                      data-testid="portal-delivery-date-input"
                    />
                    <p className="text-[11px] text-muted-foreground">{t("orders.deliveryDaysHint", { days: weekdayNames(deliveryDays) })}</p>
                  </div>
                  <Button className="w-full" onClick={placeOrder} disabled={placing} data-testid="portal-place-order">
                    {t("client.placeOrder")}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </ClientLayout>
  );
}
